import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import Select from "react-select";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import "../compnay/ManageCompany.css";

const apiBaseUrl =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

const mobileApiBaseUrl =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_MOBILE_API_URL
    : process.env.REACT_APP_MOBILE_API_URL_Production;

const proofUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const base = (mobileApiBaseUrl || apiBaseUrl || "")
    .replace(/\/api\/?$/, "")
    .replace(/\/$/, "");
  return `${base}/${path.replace(/\\/g, "/").replace(/^\//, "")}`;
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Issued", label: "Issued" },
  { value: "PaymentSubmitted", label: "Payment submitted" },
  { value: "Settled", label: "Settled" },
  { value: "Rejected", label: "Rejected" },
];

const formatMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusBadgeClass = (status) => {
  const map = {
    Issued: "bg-info",
    PaymentSubmitted: "bg-warning text-dark",
    Settled: "bg-success",
    Rejected: "bg-danger",
    Draft: "bg-secondary",
  };
  return map[status] || "bg-secondary";
};

const DetailField = ({ label, value, isDarkMode }) => (
  <div
    className="p-3 h-100"
    style={{
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
      background: isDarkMode ? "#1f2937" : "#f9fafb",
    }}
  >
    <div
      className="mb-1"
      style={{
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        color: isDarkMode ? "#9ca3af" : "#6b7280",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: "14px",
        fontWeight: 600,
        color: isDarkMode ? "#ffffff" : "#111827",
        wordBreak: "break-word",
      }}
    >
      {value ?? "—"}
    </div>
  </div>
);

const ManageRiderRemittances = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [businessDate, setBusinessDate] = useState("");
  const [generateDate, setGenerateDate] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [riderOptions, setRiderOptions] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [manualAmount, setManualAmount] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [riderCodOwed, setRiderCodOwed] = useState(null);
  const [loadingRiders, setLoadingRiders] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { PageNumber: pageNumber, PageSize: pageSize.value };
      if (statusFilter?.value) params.Status = statusFilter.value;
      if (businessDate) params.BusinessDate = businessDate;
      const response = await axiosInstance.get(APIs.GET_RIDER_REMITTANCE_INVOICES, { params });
      if (response.data.status) {
        setInvoices(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || "Failed to load remittances");
      }
    } catch {
      showErrorToast("Failed to load remittances");
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize.value, statusFilter?.value, businessDate]);

  useEffect(() => {
    const handler = setTimeout(() => fetchInvoices(), 300);
    return () => clearTimeout(handler);
  }, [fetchInvoices]);

  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, businessDate, pageSize.value]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const params = {};
      if (generateDate) params.BusinessDate = generateDate;
      const response = await axiosInstance.post(APIs.POST_GENERATE_RIDER_REMITTANCE_INVOICES, null, { params });
      if (response.data.status) {
        const d = response.data.data || {};
        showSuccessToast(`Generated ${d.invoicesCreated || 0} invoice(s), ${d.linesAdded || 0} line(s)`);
        fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Generate failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Generate failed");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RIDER_REMITTANCE_INVOICE_BY_ID, {
        params: { RemittanceInvoice_ID: id },
      });
      if (response.data.status) {
        setDetailInvoice(response.data.data);
        setReviewNote("");
      } else {
        showErrorToast("Failed to load invoice detail");
      }
    } catch {
      showErrorToast("Failed to load invoice detail");
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (action) => {
    if (!detailInvoice) return;
    const id = detailInvoice.remittanceInvoiceId || detailInvoice.RemittanceInvoice_ID;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_REVIEW_RIDER_REMITTANCE_INVOICE, {
        remittanceInvoice_ID: id,
        action,
        reviewNote: reviewNote || null,
      });
      if (response.data.status) {
        showSuccessToast(action === "approve" ? "Remittance approved" : "Remittance rejected");
        setDetailInvoice(null);
        fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Review failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Review failed");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter(STATUS_OPTIONS[0]);
    setBusinessDate("");
    setGenerateDate("");
  };

  const openCreateModal = async () => {
    setCreateModalOpen(true);
    setSelectedRider(null);
    setManualAmount("");
    setManualNote("");
    setRiderCodOwed(null);
    try {
      setLoadingRiders(true);
      const response = await axiosInstance.get(APIs.GET_ALL_MARKETPLACE_RIDERS, {
        params: {
          ApprovalStatus: "Approved",
          SortBy: "FirstName",
          SortOrder: "ASC",
          PageSize: 500,
          PageNumber: 1,
        },
      });
      if (response.data?.status) {
        const list = Array.isArray(response.data.data) ? response.data.data : [];
        setRiderOptions(
          list.map((rider) => {
            const id = rider.marketplaceRiderId || rider.marketplaceRider_ID;
            const name =
              rider.name ||
              `${rider.firstName || ""} ${rider.lastName || ""}`.trim() ||
              `Rider #${id}`;
            const phone = rider.phone || rider.Phone || "";
            return {
              value: id,
              label: phone ? `${name} (${phone})` : name,
            };
          })
        );
      } else {
        setRiderOptions([]);
        showErrorToast(response.data?.message || "Failed to load riders");
      }
    } catch {
      setRiderOptions([]);
      showErrorToast("Failed to load riders");
    } finally {
      setLoadingRiders(false);
    }
  };

  const handleRiderSelect = async (option) => {
    setSelectedRider(option);
    setRiderCodOwed(null);
    setManualAmount("");
    if (!option?.value) return;

    try {
      const response = await axiosInstance.get(APIs.GET_RIDER_WALLET, {
        params: { MarketplaceRider_ID: option.value, RecentLedgerCount: 1 },
      });
      if (response.data?.status) {
        const owed = Number(response.data?.data?.codOwed ?? 0);
        setRiderCodOwed(owed);
        if (owed > 0) setManualAmount(String(Number(owed.toFixed(2))));
      } else {
        showErrorToast(response.data?.message || "Failed to load rider wallet");
      }
    } catch {
      showErrorToast("Failed to load rider wallet");
    }
  };

  const handleCreateManualRemittance = async () => {
    if (!selectedRider?.value) {
      showErrorToast("Please select a rider");
      return;
    }
    const amount = Number(manualAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showErrorToast("Enter a remittance amount greater than zero");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_CREATE_RIDER_ADHOC_REMITTANCE, {
        marketplaceRider_ID: selectedRider.value,
        amount,
        note: manualNote.trim() || null,
      });
      if (response.data?.status) {
        showSuccessToast(response.data.message || "Manual remittance created");
        setCreateModalOpen(false);
        fetchInvoices();
      } else {
        showErrorToast(response.data?.message || "Failed to create remittance");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to create remittance");
    } finally {
      setLoading(false);
    }
  };

  const filterInputStyle = {
    backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.9)" : "rgba(255, 255, 255, 0.9)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(255, 255, 255, 0.3)",
    color: isDarkMode ? "#ffffff" : "#333",
    borderRadius: "6px",
    height: "36px",
  };

  const labelStyle = {
    color: isDarkMode ? "#e5e7eb" : "#374151",
  };

  const inputStyle = {
    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
    border: isDarkMode ? "1px solid #4b5563" : "1px solid #d1d5db",
    color: isDarkMode ? "#ffffff" : "#111827",
    borderRadius: "10px",
  };

  const panelStyle = {
    borderRadius: "10px",
    border: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
    background: isDarkMode ? "#1f2937" : "#f9fafb",
  };

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Rider COD Remittances</li>
          </ol>
          <h1 className="page-header mb-0">Rider COD Remittances</h1>
        </div>
        <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
          <input
            type="date"
            className="form-control form-control-sm"
            value={generateDate}
            onChange={(e) => setGenerateDate(e.target.value)}
            title="Business date (optional)"
            style={{ width: "160px", height: "36px" }}
          />
          <button
            type="button"
            className="btn px-4"
            onClick={handleGenerate}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-file-invoice fa-lg me-2 ms-n2 text-white"></i>
            Generate Daily Invoices
          </button>
          <button
            type="button"
            className="btn px-4"
            onClick={openCreateModal}
            style={{
              backgroundColor: isDarkMode ? "#374151" : "#111827",
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <i className="fa fa-user-plus fa-lg me-2 ms-n2 text-white"></i>
            Create Manual Remittance
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: `0 4px 20px ${themeColor}40`,
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    <div className="col-xl-3 col-lg-4 col-md-6">
                      <input
                        type="date"
                        className="filter-input form-control"
                        value={businessDate}
                        onChange={(e) => setBusinessDate(e.target.value)}
                        title="Filter by business date"
                        style={filterInputStyle}
                      />
                    </div>
                    <div className="col-xl-3 col-lg-4 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={statusFilter}
                          onChange={setStatusFilter}
                          options={STATUS_OPTIONS}
                          styles={dropdownStyles}
                          placeholder="All statuses"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <PageSizeDropDownSelection
                          defaultValue={pageSize}
                          onChange={setPageSize}
                          styles={dropdownStyles}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-12">
                      <button
                        type="button"
                        className="btn"
                        onClick={clearFilters}
                        title="Clear all filters"
                        style={{
                          height: "36px",
                          backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa fa-times me-1"></i>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="table-responsive table-container"
              style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto", position: "relative", zIndex: 1 }}
            >
              <table className="table table-hover text-nowrap">
                <thead
                  className="sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10000,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2">ID</th>
                    <th className="pt-2 pb-2">Type</th>
                    <th className="pt-2 pb-2">Rider</th>
                    <th className="pt-2 pb-2">Business Date</th>
                    <th className="pt-2 pb-2">Orders</th>
                    <th className="pt-2 pb-2">COD Total</th>
                    <th className="pt-2 pb-2">Status</th>
                    <th className="pt-2 pb-2">Paid Amount</th>
                    <th className="pt-2 pb-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length > 0 ? (
                    invoices.map((item) => {
                      const id = item.remittanceInvoiceId || item.RemittanceInvoice_ID;
                      const status = item.status || item.Status;
                      const invoiceType = item.invoiceType || item.InvoiceType || "Daily";
                      const isAdHoc = String(invoiceType).toLowerCase() === "adhoc";
                      return (
                        <tr key={id}>
                          <td className="align-middle">{id}</td>
                          <td className="align-middle">
                            <span className={`badge ${isAdHoc ? "bg-primary" : "bg-secondary"}`}>
                              {isAdHoc ? "Pay now" : "Daily"}
                            </span>
                          </td>
                          <td className="align-middle">{item.riderName || `${item.riderFirstName || ""} ${item.riderLastName || ""}`.trim()}</td>
                          <td className="align-middle">{item.businessDate || item.BusinessDate}</td>
                          <td className="align-middle">{item.orderCount ?? item.OrderCount}</td>
                          <td className="align-middle">{formatMoney(item.totalCodAmount ?? item.TotalCodAmount)}</td>
                          <td className="align-middle">
                            <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
                          </td>
                          <td className="align-middle">{item.riderPaidAmount != null ? formatMoney(item.riderPaidAmount) : "—"}</td>
                          <td className="align-middle text-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDetail(id)}
                              title="View invoice"
                              style={{
                                width: "32px",
                                height: "32px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "6px",
                                border: `1px solid ${themeColor}`,
                                color: themeColor,
                              }}
                            >
                              <i className="fa fa-eye" style={{ fontSize: "12px" }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className="align-middle text-center" style={{ fontSize: "18px" }}>
                        {loading ? "Loading remittance invoices..." : "No remittance invoices found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRecords={totalRecords}
              pageSize={parseInt(pageSize.value, 10) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>

      <CustomModal
        title="Remittance Invoice"
        subtitle={
          detailInvoice
            ? `${detailInvoice.riderName || "Rider"} · ${detailInvoice.businessDate || ""}`
            : "Invoice details"
        }
        isOpen={!!detailInvoice}
        onClose={() => setDetailInvoice(null)}
        isCentered
        modalSize="modal-lg"
        showButton={detailInvoice?.status !== "PaymentSubmitted"}
        showCancelButton={false}
        showSubmitButton={detailInvoice?.status !== "PaymentSubmitted"}
        submitButtonText="Close"
        onSubmit={() => setDetailInvoice(null)}
        minHeight="280px"
        maxHeight="85vh"
      >
        {detailInvoice && (() => {
          const status = detailInvoice.status || detailInvoice.Status;
          const invoiceType = detailInvoice.invoiceType || detailInvoice.InvoiceType || "Daily";
          const isAdHoc = String(invoiceType).toLowerCase() === "adhoc";
          const invoiceId = detailInvoice.remittanceInvoiceId || detailInvoice.RemittanceInvoice_ID;
          const proofPath = detailInvoice.proofImagePath || "";
          const proofHref = proofUrl(proofPath);
          const isPdf = String(proofPath).toLowerCase().endsWith(".pdf");
          const canReview = status === "PaymentSubmitted";

          return (
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span
                  className={`badge ${statusBadgeClass(status)}`}
                  style={{ fontSize: "12px", padding: "6px 10px" }}
                >
                  {status || "—"}
                </span>
                <span
                  className={`badge ${isAdHoc ? "bg-primary" : "bg-secondary"}`}
                  style={{ fontSize: "12px", padding: "6px 10px" }}
                >
                  {isAdHoc ? "Pay now" : "Daily"}
                </span>
                <span style={{ fontSize: "13px", color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
                  Invoice #{invoiceId}
                </span>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <DetailField
                    label="Rider"
                    value={detailInvoice.riderName || "—"}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className="col-md-6">
                  <DetailField
                    label="Phone"
                    value={detailInvoice.riderPhone || "—"}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className="col-md-6">
                  <DetailField
                    label="Business Date"
                    value={detailInvoice.businessDate || "—"}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className="col-md-6">
                  <DetailField
                    label="Orders"
                    value={detailInvoice.orderCount ?? detailInvoice.OrderCount ?? 0}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className="col-md-6">
                  <DetailField
                    label="COD Total"
                    value={formatMoney(detailInvoice.totalCodAmount)}
                    isDarkMode={isDarkMode}
                  />
                </div>
                <div className="col-md-6">
                  <DetailField
                    label="Rider Paid"
                    value={
                      detailInvoice.riderPaidAmount != null
                        ? formatMoney(detailInvoice.riderPaidAmount)
                        : "—"
                    }
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>

              {detailInvoice.riderNote && (
                <div className="mb-3 p-3" style={panelStyle}>
                  <div
                    className="mb-1"
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: isDarkMode ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Rider note
                  </div>
                  <div style={{ color: isDarkMode ? "#ffffff" : "#111827" }}>
                    {detailInvoice.riderNote}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0" style={{ fontWeight: 700, color: isDarkMode ? "#fff" : "#111827" }}>
                    Payment proof
                  </h6>
                  {proofHref && (
                    <a
                      href={proofHref}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: "13px", color: themeColor }}
                    >
                      Open in new tab
                    </a>
                  )}
                </div>
                {proofHref ? (
                  <div
                    className="p-3"
                    style={{
                      ...panelStyle,
                      textAlign: "center",
                    }}
                  >
                    {isPdf ? (
                      <a
                        href={proofHref}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm"
                        style={{
                          background: themeColor,
                          color: "#fff",
                          borderRadius: "8px",
                        }}
                      >
                        <i className="fa fa-file-pdf me-2" />
                        Open PDF proof
                      </a>
                    ) : (
                      <img
                        src={proofHref}
                        alt="Payment proof"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) fallback.style.display = "block";
                        }}
                        style={{
                          maxWidth: "100%",
                          maxHeight: 280,
                          borderRadius: "8px",
                          border: isDarkMode ? "1px solid #4b5563" : "1px solid #e5e7eb",
                        }}
                      />
                    )}
                    <div
                      style={{
                        display: "none",
                        color: isDarkMode ? "#9ca3af" : "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Proof image could not be loaded. Use “Open in new tab”.
                    </div>
                  </div>
                ) : (
                  <div
                    className="text-center py-4"
                    style={{
                      ...panelStyle,
                      borderStyle: "dashed",
                    }}
                  >
                    <i
                      className="fa fa-image mb-2"
                      style={{ fontSize: "22px", color: isDarkMode ? "#6b7280" : "#9ca3af" }}
                    />
                    <div style={{ fontWeight: 600, color: isDarkMode ? "#e5e7eb" : "#374151" }}>
                      No payment proof uploaded
                    </div>
                  </div>
                )}
              </div>

              {Array.isArray(detailInvoice.lines) && detailInvoice.lines.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0" style={{ fontWeight: 700, color: isDarkMode ? "#fff" : "#111827" }}>
                      Order lines
                    </h6>
                    <span style={{ fontSize: "12px", color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
                      {detailInvoice.lines.length} order(s)
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>Restaurant</th>
                          <th>Order Total</th>
                          <th>Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailInvoice.lines.map((line) => (
                          <tr key={line.lineId || line.Line_ID}>
                            <td>{line.invoiceNo || line.Recvorder_InvoiceNo}</td>
                            <td>{line.compName || "—"}</td>
                            <td>{formatMoney(line.orderTotal ?? line.OrderTotal)}</td>
                            <td>
                              {line.completedAt
                                ? new Date(line.completedAt).toLocaleString()
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {canReview && (
                <div className="p-3" style={panelStyle}>
                  <label className="form-label fw-semibold" style={labelStyle}>
                    Review note
                  </label>
                  <textarea
                    className="form-control mb-3"
                    rows={2}
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Optional note for approve/reject"
                    style={inputStyle}
                  />
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleReview("reject")}
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                        borderRadius: "8px",
                        fontWeight: 600,
                        minWidth: "110px",
                      }}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleReview("approve")}
                      style={{
                        background: themeColor,
                        color: "#fff",
                        borderRadius: "8px",
                        fontWeight: 600,
                        minWidth: "110px",
                      }}
                    >
                      Approve
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </CustomModal>

      <CustomModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create Manual Remittance"
        subtitle="Select a rider and create a COD remittance invoice manually."
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText="Create Remittance"
        onSubmit={handleCreateManualRemittance}
        modalSize="modal-md"
        isCentered={true}
        minHeight="260px"
        maxHeight="80vh"
      >
        <div className="mb-3">
          <label className="form-label fw-semibold" style={labelStyle}>
            Rider
          </label>
          <Select
            value={selectedRider}
            onChange={handleRiderSelect}
            options={riderOptions}
            styles={dropdownStyles}
            placeholder={loadingRiders ? "Loading riders..." : "Select rider"}
            isClearable
            isLoading={loadingRiders}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {selectedRider && (
          <div className="mb-3 p-3" style={panelStyle}>
            <div style={{ fontSize: "12px", color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
              COD owed balance
            </div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: isDarkMode ? "#ffffff" : "#111827" }}>
              {riderCodOwed == null ? "—" : formatMoney(riderCodOwed)}
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label fw-semibold" style={labelStyle}>
            Remittance amount
          </label>
          <input
            type="number"
            className="form-control"
            min="0"
            step="0.01"
            value={manualAmount}
            onChange={(e) => setManualAmount(e.target.value)}
            placeholder="Enter amount"
            style={inputStyle}
          />
          <small style={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
            Amount cannot exceed rider available COD (owed minus open invoices).
          </small>
        </div>

        <div className="mb-1">
          <label className="form-label fw-semibold" style={labelStyle}>
            Note (optional)
          </label>
          <textarea
            className="form-control"
            rows={2}
            value={manualNote}
            onChange={(e) => setManualNote(e.target.value)}
            placeholder="Optional note for this remittance"
            style={inputStyle}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default ManageRiderRemittances;
